/**
 * @Author: Claude Code
 * @Date: 2025-11-21
 * @FilePath: src/services/videoProgressService.js
 * @Description: 视频上传进度 SignalR 服务 - 支持多设备订阅
 */
import * as signalR from '@microsoft/signalr'
import apiSetting from '@/api/apiSetting'

/**
 * 视频上传进度 SignalR 服务
 * 单例模式，支持多设备同时订阅
 *
 * Hub: /hubs/video-progress
 * 订阅方法: SubscribeProgress(imei) / UnsubscribeProgress(imei) / SubscribeMultipleProgress(imeis[])
 * 事件: UploadProgress, UploadCompleted, UploadCanceled, Subscribed, Unsubscribed, SubscribeError
 */
class VideoProgressService {
  constructor() {
    this.connection = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
    this.subscribers = new Map() // 存储订阅信息: Map<imei, { onProgress, onCompleted }>
    this.globalProgressCallbacks = [] // 全局进度回调（不按设备区分）
    this.globalCompletedCallbacks = [] // 全局完成回调
    this.globalCanceledCallbacks = [] // 全局取消回调
    this.eventHandlerRegistered = false // 标记事件处理器是否已注册
    this.heartbeatTimer = null
    this.recentProgressEvents = new Map()
    this.recentCompletedEvents = new Map()
    this.recentCanceledEvents = new Map()
  }

  /**
   * 获取 Hub URL
   * @returns {string}
   */
  getHubUrl() {
    const baseUrl = apiSetting.gethttp().replace(/\/$/, '')
    return `${baseUrl}/hubs/video-progress`
  }

  /**
   * 初始化并连接到 SignalR Hub
   * @param {string} hubUrl - SignalR Hub 地址（可选，不传则自动获取）
   * @returns {Promise<boolean>}
   */
  async connect(hubUrl) {
    if (this.isConnected) {
      console.log('[VideoProgress] 已经连接')
      return true
    }

    const url = hubUrl || this.getHubUrl()
    console.log('[VideoProgress] 正在连接:', url)

    try {
      // 获取 token
      const token = localStorage.getItem('ms_token')

      // 创建连接
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: token ? () => token : undefined,
          withCredentials: false,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // 自定义重连策略: 0s → 2s → 5s → 10s → 30s
            if (retryContext.previousRetryCount === 0) return 0
            if (retryContext.previousRetryCount === 1) return 2000
            if (retryContext.previousRetryCount === 2) return 5000
            if (retryContext.previousRetryCount === 3) return 10000
            return 30000
          },
        })
        .configureLogging(
          process.env.NODE_ENV === 'development' ? signalR.LogLevel.Debug : signalR.LogLevel.Warning
        )
        .build()

      // ===== 连接生命周期事件 =====

      // 连接关闭
      this.connection.onclose((error) => {
        console.warn('[VideoProgress] 连接关闭', error)
        this.isConnected = false
        this.eventHandlerRegistered = false
        this.stopHeartbeat()

        // 触发全局事件
        window.dispatchEvent(new CustomEvent('signalr-video-disconnected', { detail: { error } }))
      })

      // 开始重连
      this.connection.onreconnecting((error) => {
        console.warn('[VideoProgress] 正在重连...', error)
        this.isConnected = false
        this.reconnectAttempts++

        // 超过最大重连次数时通知
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('[VideoProgress] 达到最大重连次数')
          window.dispatchEvent(
            new CustomEvent('signalr-video-reconnect-failed', {
              detail: { attempts: this.reconnectAttempts },
            })
          )
        }
      })

      // 重连成功
      this.connection.onreconnected((connectionId) => {
        console.log('[VideoProgress] 重连成功, ConnectionId:', connectionId)
        this.isConnected = true
        this.reconnectAttempts = 0

        // 重连后重新订阅之前的所有设备
        this.resubscribeAll()

        // 重启心跳
        this.startHeartbeat()

        // 触发全局事件
        window.dispatchEvent(new CustomEvent('signalr-video-reconnected', { detail: { connectionId } }))
      })

      // 启动连接
      await this.connection.start()
      this.isConnected = true

      // 注册全局事件处理器（只注册一次）
      this.registerEventHandler()

      // 启动心跳检测
      this.startHeartbeat()

      console.log('[VideoProgress] 连接成功, ConnectionId:', this.connection.connectionId)

      return true
    } catch (error) {
      console.error('[VideoProgress] 连接失败:', error)
      this.isConnected = false
      return false
    }
  }

  /**
   * 注册 SignalR 事件处理器（只注册一次）
   * 监听 UploadProgress, UploadCompleted, UploadCanceled, Subscribed, Unsubscribed, SubscribeError 事件
   * @private
   */
  registerEventHandler() {
    if (this.eventHandlerRegistered) {
      return
    }

    // 监听 Subscribed 事件 - 订阅成功
    this.connection.on('Subscribed', (data) => {
      console.log('[VideoProgress] 订阅成功:', data)
    })

    // 监听 Unsubscribed 事件 - 取消订阅成功
    this.connection.on('Unsubscribed', (data) => {
      console.log('[VideoProgress] 取消订阅成功:', data)
    })

    // 监听 SubscribeError 事件 - 订阅失败
    this.connection.on('SubscribeError', (data) => {
      console.error('[VideoProgress] 订阅失败:', data)
    })

    // 监听 UploadProgress 事件 - 进度更新
    // data 结构: { imei, fileName, progress, completionTime }
    this.connection.on('UploadProgress', (data) => {
      const imei = data.imei || data.Imei
      const fileName = data.fileName || data.FileName || data.filename

      if (!imei) {
        console.warn('[VideoProgress] 收到的进度数据缺少 IMEI', data)
        return
      }

      if (this.isDuplicateEvent(this.recentProgressEvents, `${imei}|${fileName}|${data.progress ?? data.Progress}`)) {
        return
      }

      console.log(`[VideoProgress] 进度更新: ${imei} - ${fileName}: ${data.progress}%`)

      // 调用设备特定回调
      const callbacks = this.subscribers.get(imei)
      if (callbacks && typeof callbacks.onProgress === 'function') {
        try {
          callbacks.onProgress(data)
        } catch (error) {
          console.error(`[VideoProgress] 设备 ${imei} 进度回调执行失败:`, error)
        }
      }

      // 调用全局进度回调
      console.log(`[VideoProgress] 全局回调数量: ${this.globalProgressCallbacks.length}`)
      this.globalProgressCallbacks.forEach(cb => {
        try {
          cb(data)
        } catch (error) {
          console.error('[VideoProgress] 全局进度回调执行失败:', error)
        }
      })
    })

    // 监听 UploadCompleted 事件 - 上传完成
    // data 结构: { imei, fileName }
    this.connection.on('UploadCompleted', (data) => {
      const imei = data.imei || data.Imei
      const fileName = data.fileName || data.FileName || data.filename

      if (!imei) {
        console.warn('[VideoProgress] 收到的完成数据缺少 IMEI', data)
        return
      }

      if (this.isDuplicateEvent(this.recentCompletedEvents, `${imei}|${fileName}`)) {
        return
      }

      console.log(`[VideoProgress] 上传完成: ${imei} - ${fileName}`)

      // 调用设备特定回调
      const callbacks = this.subscribers.get(imei)
      if (callbacks && typeof callbacks.onCompleted === 'function') {
        try {
          callbacks.onCompleted(data)
        } catch (error) {
          console.error(`[VideoProgress] 设备 ${imei} 完成回调执行失败:`, error)
        }
      }

      // 调用全局完成回调
      this.globalCompletedCallbacks.forEach(cb => {
        try {
          cb(data)
        } catch (error) {
          console.error('[VideoProgress] 全局完成回调执行失败:', error)
        }
      })
    })

    // 监听 UploadCanceled 事件 - 上传取消
    // data 结构: { imei, fileName, startTime, endTime, channel, canceledAt }
    this.connection.on('UploadCanceled', (data) => {
      const imei = data.imei || data.Imei
      const fileName = data.fileName || data.FileName || data.filename

      if (!imei) {
        console.warn('[VideoProgress] 收到的取消数据缺少 IMEI', data)
        return
      }

      if (this.isDuplicateEvent(this.recentCanceledEvents, `${imei}|${fileName}`)) {
        return
      }

      console.log(`[VideoProgress] 上传取消: ${imei} - ${fileName}`)

      this.globalCanceledCallbacks.forEach(cb => {
        try {
          cb(data)
        } catch (error) {
          console.error('[VideoProgress] 全局取消回调执行失败:', error)
        }
      })
    })

    this.eventHandlerRegistered = true
    console.log('[VideoProgress] 事件处理器已注册 (Subscribed, Unsubscribed, SubscribeError, UploadProgress, UploadCompleted, UploadCanceled)')
  }

  isDuplicateEvent(cache, key, windowMs = 1000) {
    if (!key) return false

    const now = Date.now()
    const lastTime = cache.get(key)
    cache.set(key, now)

    cache.forEach((value, mapKey) => {
      if (now - value > windowMs) {
        cache.delete(mapKey)
      }
    })

    return lastTime !== undefined && now - lastTime <= windowMs
  }

  /**
   * 订阅指定设备的进度推送
   * @param {string} imei - 设备IMEI号
   * @param {Function} onProgress - 收到进度时的回调函数
   * @param {Function} onCompleted - 上传完成时的回调函数（可选）
   * @returns {Promise<boolean>}
   */
  async subscribeToVideo(imei, onProgress, onCompleted) {
    if (!this.connection || !this.isConnected) {
      throw new Error('[VideoProgress] 未连接，请先调用 connect()')
    }

    if (!imei) {
      throw new Error('[VideoProgress] IMEI 不能为空')
    }

    if (typeof onProgress !== 'function') {
      throw new Error('[VideoProgress] onProgress 必须是一个函数')
    }

    try {
      // 如果已经订阅，先取消旧的订阅
      if (this.subscribers.has(imei)) {
        console.warn(`[VideoProgress] 设备 ${imei} 已订阅，将更新回调函数`)
      }

      // 调用服务端方法订阅 - SubscribeProgress(imei)
      await this.connection.invoke('SubscribeProgress', imei)

      // 保存订阅信息
      this.subscribers.set(imei, {
        onProgress,
        onCompleted: onCompleted || null
      })

      console.log(`[VideoProgress] 已订阅设备 ${imei} (当前订阅数: ${this.subscribers.size})`)
      return true
    } catch (error) {
      console.error(`[VideoProgress] 订阅设备 ${imei} 失败:`, error)
      throw error
    }
  }

  /**
   * 批量订阅多个设备的进度推送
   * @param {string[]} imeis - 设备IMEI号数组
   * @param {Function} onProgress - 收到进度时的回调函数
   * @param {Function} onCompleted - 上传完成时的回调函数（可选）
   * @returns {Promise<boolean>}
   */
  async subscribeMultiple(imeis, onProgress, onCompleted) {
    if (!this.connection || !this.isConnected) {
      throw new Error('[VideoProgress] 未连接，请先调用 connect()')
    }

    if (!Array.isArray(imeis) || imeis.length === 0) {
      throw new Error('[VideoProgress] IMEI 数组不能为空')
    }

    try {
      // 调用服务端批量订阅方法 - SubscribeMultipleProgress(imeis[])
      await this.connection.invoke('SubscribeMultipleProgress', imeis)

      // 保存订阅信息
      imeis.forEach(imei => {
        this.subscribers.set(imei, {
          onProgress: onProgress || (() => {}),
          onCompleted: onCompleted || null
        })
      })

      console.log(`[VideoProgress] 已批量订阅 ${imeis.length} 个设备 (当前订阅数: ${this.subscribers.size})`)
      return true
    } catch (error) {
      console.error(`[VideoProgress] 批量订阅失败:`, error)
      throw error
    }
  }

  /**
   * 兼容旧接口 - 订阅进度
   * @deprecated 请使用 subscribeToVideo
   */
  async subscribeProgress(imei, callback) {
    return this.subscribeToVideo(imei, callback, null)
  }

  /**
   * 取消订阅
   * @param {string} imei - 设备IMEI号
   * @returns {Promise<void>}
   */
  async unsubscribeFromVideo(imei) {
    if (!imei) {
      console.warn('[VideoProgress] IMEI 不能为空')
      return
    }

    if (!this.connection || !this.isConnected) {
      console.warn('[VideoProgress] 未连接，无法取消订阅')
      // 仍然移除本地订阅信息
      this.subscribers.delete(imei)
      return
    }

    try {
      // 调用服务端方法取消订阅 - UnsubscribeProgress(imei)
      await this.connection.invoke('UnsubscribeProgress', imei)

      // 移除本地订阅信息
      this.subscribers.delete(imei)

      console.log(`[VideoProgress] 已取消订阅设备 ${imei} (剩余订阅数: ${this.subscribers.size})`)
    } catch (error) {
      console.error(`[VideoProgress] 取消订阅设备 ${imei} 失败:`, error)
      // 即使服务端调用失败，也要移除本地订阅
      this.subscribers.delete(imei)
      throw error
    }
  }

  /**
   * 兼容旧接口 - 取消订阅
   * @deprecated 请使用 unsubscribeFromVideo
   */
  async unsubscribeProgress(imei) {
    return this.unsubscribeFromVideo(imei)
  }

  /**
   * 注册全局进度回调（接收所有设备的进度更新）
   * @param {Function} callback - 回调函数
   */
  onGlobalProgress(callback) {
    if (typeof callback === 'function') {
      this.globalProgressCallbacks.push(callback)
      console.log(`[VideoProgress] 注册全局进度回调，当前回调数量: ${this.globalProgressCallbacks.length}`)
    }
  }

  /**
   * 移除全局进度回调
   * @param {Function} callback - 要移除的回调函数
   */
  offGlobalProgress(callback) {
    const index = this.globalProgressCallbacks.indexOf(callback)
    if (index > -1) {
      this.globalProgressCallbacks.splice(index, 1)
    }
  }

  /**
   * 注册全局完成回调（接收所有设备的完成通知）
   * @param {Function} callback - 回调函数
   */
  onGlobalCompleted(callback) {
    if (typeof callback === 'function') {
      this.globalCompletedCallbacks.push(callback)
    }
  }

  /**
   * 移除全局完成回调
   * @param {Function} callback - 要移除的回调函数
   */
  offGlobalCompleted(callback) {
    const index = this.globalCompletedCallbacks.indexOf(callback)
    if (index > -1) {
      this.globalCompletedCallbacks.splice(index, 1)
    }
  }

  /**
   * 注册全局取消回调（接收所有设备的取消通知）
   * @param {Function} callback - 回调函数
   */
  onGlobalCanceled(callback) {
    if (typeof callback === 'function') {
      this.globalCanceledCallbacks.push(callback)
    }
  }

  /**
   * 移除全局取消回调
   * @param {Function} callback - 要移除的回调函数
   */
  offGlobalCanceled(callback) {
    const index = this.globalCanceledCallbacks.indexOf(callback)
    if (index > -1) {
      this.globalCanceledCallbacks.splice(index, 1)
    }
  }

  /**
   * 重连后重新订阅所有设备
   * @private
   */
  async resubscribeAll() {
    if (this.subscribers.size === 0) {
      console.log('[VideoProgress] 没有需要重新订阅的设备')
      return
    }

    console.log(`[VideoProgress] 重连后重新订阅 ${this.subscribers.size} 个设备...`)

    const imeis = Array.from(this.subscribers.keys())

    // 使用批量订阅方法
    try {
      await this.connection.invoke('SubscribeMultipleProgress', imeis)
      console.log(`[VideoProgress] 批量重新订阅 ${imeis.length} 个设备成功`)
    } catch (error) {
      console.error('[VideoProgress] 批量重新订阅失败，尝试逐个订阅:', error)
      // 如果批量订阅失败，尝试逐个订阅
      for (const imei of imeis) {
        try {
          await this.connection.invoke('SubscribeProgress', imei)
          console.log(`[VideoProgress] 重新订阅设备 ${imei} 成功`)
        } catch (err) {
          console.error(`[VideoProgress] 重新订阅设备 ${imei} 失败:`, err)
        }
      }
    }
  }

  /**
   * 获取本地订阅的设备列表
   * @returns {string[]}
   */
  getSubscribedDevices() {
    return Array.from(this.subscribers.keys())
  }

  /**
   * 检查设备是否已订阅
   * @param {string} imei - 设备IMEI号
   * @returns {boolean}
   */
  isDeviceSubscribed(imei) {
    return this.subscribers.has(imei)
  }

  /**
   * 断开连接
   * @returns {Promise<void>}
   */
  async disconnect() {
    if (this.connection) {
      try {
        // 停止心跳
        this.stopHeartbeat()

        // 取消所有订阅
        const imeis = Array.from(this.subscribers.keys())
        for (const imei of imeis) {
          await this.unsubscribeFromVideo(imei)
        }

        // 停止连接
        await this.connection.stop()
        console.log('[VideoProgress] 已断开连接')
      } catch (error) {
        console.error('[VideoProgress] 断开连接失败:', error)
      }
    }

    this.isConnected = false
    this.connection = null
    this.subscribers.clear()
    this.globalProgressCallbacks = []
    this.globalCompletedCallbacks = []
    this.globalCanceledCallbacks = []
    this.eventHandlerRegistered = false
  }

  /**
   * 启动心跳检测
   * @private
   */
  startHeartbeat() {
    // 后端未实现 Ping 方法，暂时禁用心跳
    // 如果后端实现了心跳方法，可以在这里启用
    console.log('[VideoProgress] 心跳检测已禁用（后端未实现 Ping 方法）')
  }

  /**
   * 停止心跳检测
   * @private
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * 检查连接状态
   * @returns {boolean}
   */
  checkConnection() {
    return this.isConnected && this.connection !== null
  }

  /**
   * 获取连接ID
   * @returns {string|null}
   */
  getConnectionId() {
    return this.connection?.connectionId || null
  }

  /**
   * 获取连接状态枚举值
   * @returns {string} Disconnected | Connected | Connecting | Reconnecting
   */
  getConnectionState() {
    if (!this.connection) return 'Disconnected'

    const state = this.connection.state
    return signalR.HubConnectionState[state] || 'Unknown'
  }
}

// 导出单例
export default new VideoProgressService()
